import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import HelperTextContainer from '@/components/HelperTextContainer';
import { genderLists } from '../Forms/defaults';
import { useGetEthnicity } from '@/api/ethnicity';
import { usePatientContext } from './usePatientContext';

const PatientInformationForm = () => {
  const {
    mode,
    form: {
      control,
      register,
      formState: { errors },
    },
  } = usePatientContext();

  const [, setSelectedEthnicities] = useState<string[]>([]);

  const { data } = useGetEthnicity();

  const ethnicityData = data.map((item) => item.name);

  return (
    <Box>
      <Stack direction="column" spacing={3}>
        <Stack spacing={0.25}>
          <Typography variant="h3">Identity and demographics</Typography>
          <Typography variant="body2" color="text.secondary">
            Record the information used to identify and contextualize this
            patient.
          </Typography>
        </Stack>
        <Stack spacing={2.5} direction={{ xs: 'column', md: 'row' }}>
          <FormControl fullWidth margin="none">
            <Controller
              name="name"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  variant="outlined"
                  {...register('name')}
                  fullWidth
                  sx={{ backgroundColor: 'background.paper' }}
                />
              )}
            />
            <HelperTextContainer>
              <span>{errors.name?.message}</span>
            </HelperTextContainer>
          </FormControl>
        </Stack>
        <Stack spacing={2.5} direction={{ xs: 'column', md: 'row' }}>
          <FormControl fullWidth margin="none">
            <Controller
              name="identification"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Identification (IC/Passport)"
                  variant="outlined"
                  {...register('identification')}
                  fullWidth
                  InputProps={{
                    readOnly: mode === 'update',
                  }}
                  sx={{
                    backgroundColor:
                      mode === 'update'
                        ? 'action.disabledBackground'
                        : 'background.paper',
                  }}
                />
              )}
            />
            <HelperTextContainer>
              <span>{errors.identification?.message}</span>
            </HelperTextContainer>
          </FormControl>
          <FormControl fullWidth margin="none">
            <Controller
              name="date_of_birth"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  label="Date of birth"
                  {...register('date_of_birth')}
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    readOnly: mode === 'update',
                  }}
                  sx={{
                    backgroundColor:
                      mode === 'update'
                        ? 'action.disabledBackground'
                        : 'background.paper',
                  }}
                />
              )}
            />
            <HelperTextContainer>
              <span>{errors.date_of_birth?.message}</span>
            </HelperTextContainer>
          </FormControl>
        </Stack>
        <Stack spacing={2.5} direction={{ xs: 'column', md: 'row' }}>
          <FormControl fullWidth>
            <Controller
              name="ethnicity"
              control={control}
              render={({ field }) => {
                const isLocked = mode === 'update';

                return (
                  <Autocomplete
                    multiple
                    id="ethnicity"
                    options={ethnicityData}
                    getOptionLabel={(option) => option}
                    filterSelectedOptions
                    value={
                      ethnicityData.filter(
                        (option) => field.value?.includes(option),
                      ) || []
                    }
                    open={isLocked ? false : undefined}
                    disableClearable={isLocked}
                    onChange={(_, dataC) => {
                      if (isLocked) return;
                      setSelectedEthnicities(dataC);
                      field.onChange(dataC);
                    }}
                    //  block chip delete
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          label={option}
                          onDelete={
                            isLocked
                              ? undefined
                              : getTagProps({ index }).onDelete
                          }
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Ethnicity"
                        placeholder="Ethnicity"
                        sx={(theme) => ({
                          backgroundColor: isLocked
                            ? theme.palette.action.disabledBackground
                            : theme.palette.background.paper,
                          '& .MuiInputBase-input': {
                            color: theme.palette.text.primary,
                            WebkitTextFillColor: theme.palette.text.primary,
                          },
                        })}
                        //  THIS is what actually blocks typing
                        inputProps={{
                          ...params.inputProps,
                          readOnly: isLocked,
                        }}
                      />
                    )}
                  />
                );
              }}
            />

            <HelperTextContainer>
              <span>
                {errors?.ethnicity?.length !== undefined &&
                errors?.ethnicity?.length > 1
                  ? 'Error'
                  : errors.ethnicity?.message}
              </span>
            </HelperTextContainer>
          </FormControl>

          <FormControl fullWidth size="small" variant="standard">
            <Controller
              name="gender"
              control={control}
              render={({ field: { onChange, value } }) => {
                const isLocked = mode === 'update';

                return (
                  <Autocomplete
                    options={genderLists}
                    getOptionLabel={(option) => option}
                    value={
                      genderLists.find(
                        (option) => option.toLowerCase() === value,
                      ) || null
                    }
                    open={isLocked ? false : undefined}
                    disableClearable={isLocked}
                    onChange={(_event, newValue) => {
                      if (isLocked) return;
                      onChange(newValue ? newValue.toLowerCase() : undefined);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Gender"
                        variant="outlined"
                        sx={{
                          backgroundColor: isLocked
                            ? 'action.disabledBackground'
                            : 'background.paper',
                        }}
                        // MPORTANT: must set readOnly here (not InputProps)
                        inputProps={{
                          ...params.inputProps,
                          readOnly: isLocked,
                        }}
                      />
                    )}
                  />
                );
              }}
            />

            <HelperTextContainer>
              <span>{errors.gender?.message}</span>
            </HelperTextContainer>
          </FormControl>
        </Stack>
      </Stack>
    </Box>
  );
};

export default PatientInformationForm;
